import {Table} from "react-bootstrap";
import {getDayMetric, normalizeProductName} from "../../helpers/utils";
import { caseFoldForProductMatch } from "../../lib/productNameNormalize/engine.js";
import {getProductLinePid} from "../../helpers/getProductLinePid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/uk";
import { TIMEZONE } from "../../constans";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("uk");

/** Зіставлення позиції замовлення з колонкою розбивки (агрегація по pid, як у ReportLayout). */
function productLineMatchesBreakdownColumn(productLine, col) {
    const linePid = getProductLinePid(productLine);
    if (col?.pid != null && Number.isFinite(Number(col.pid))) {
        return linePid === col.pid;
    }
    const normalizedLine = normalizeProductName(productLine.name);
    const wantFold = caseFoldForProductMatch(col?.name ?? "");
    const lineFold =
        normalizedLine != null && String(normalizedLine).trim() !== ""
            ? caseFoldForProductMatch(normalizedLine)
            : caseFoldForProductMatch(productLine.name ?? "");
    return Boolean(wantFold) && wantFold === lineFold;
}

/**
 * Emoji за WMO weather interpretation codes (Open-Meteo daily/hourly `weather_code`).
 * @see https://open-meteo.com/en/docs — таблиця WMO
 */
function weatherCodeEmoji(code) {
    const c = Number(code);
    if (!Number.isFinite(c)) return "☁️";

    if (c === 0) return "☀️";
    if (c === 1) return "🌤️";
    if (c === 2) return "⛅";
    if (c === 3) return "☁️";
    if (c === 45 || c === 48) return "🌫️";
    if (c === 51 || c === 53 || c === 55) return "🌦️";
    if (c === 56 || c === 57) return "🌨️";
    if (c === 61 || c === 63 || c === 65) return "🌧️";
    if (c === 66 || c === 67) return "🌧️";
    if (c === 71 || c === 73 || c === 75 || c === 77) return "❄️";
    if (c === 80 || c === 81 || c === 82) return "🌦️";
    if (c === 85 || c === 86) return "❄️";
    if (c >= 95 && c <= 99) return "⛈️";
    return "☁️";
}

const KYIV_WEATHER_EMOJI_STYLE = {
    fontSize: "1.5rem",
    lineHeight: 1,
};

/** Формат «+22°» як на джерелах погоди; для 0 — «0°». */
function formatSignedDegrees(n) {
    const x = Math.round(Number(n));
    if (!Number.isFinite(x)) return null;
    if (x === 0) return "0°";
    if (x < 0) return `${x}°`;
    return `+${x}°`;
}

/**
 * Історія з Open-Meteo: блок «мін.» / «макс.» + emoji за WMO code.
 * Live «зараз»: одне значення + emoji.
 */
function formatKyivWeatherCell(w) {
    if (!w || typeof w !== "object") return "—";
    if (w._liveLoading) return <span className="text-muted text-nowrap">…</span>;
    const tMax = w.tempMaxC != null ? Number(w.tempMaxC) : NaN;
    const tMean = w.tempMeanC != null ? Number(w.tempMeanC) : NaN;
    const tMin = w.tempMinC != null ? Number(w.tempMinC) : NaN;
    const displayTemp = Number.isFinite(tMax)
        ? tMax
        : Number.isFinite(tMean)
          ? tMean
          : Number.isFinite(tMin)
            ? tMin
            : NaN;
    const emoji = weatherCodeEmoji(w.weatherCode);
    const precip = Number(w.precipitationSumMm);
    const hasPrecip = Number.isFinite(precip) && precip >= 0.1;

    const tipParts = [];
    tipParts.push(w.currentNow ? "Зараз (Open-Meteo)" : "Київ (Open-Meteo)");
    if (Number.isFinite(tMin)) tipParts.push(`мін за добу ${Math.round(tMin)}°C`);
    if (Number.isFinite(tMax)) tipParts.push(`макс за добу ${Math.round(tMax)}°C`);
    if (hasPrecip) tipParts.push(`опади ${precip < 10 ? precip.toFixed(1) : Math.round(precip)} мм`);
    const title = tipParts.join(" · ");

    if (w.currentNow) {
        if (!Number.isFinite(displayTemp)) {
            return (
                <span
                    className="text-nowrap d-flex align-items-center justify-content-between gap-2 kyiv-weather-cell"
                    title={title}
                >
                    <span>—</span>
                    <span className="flex-shrink-0" style={KYIV_WEATHER_EMOJI_STYLE} aria-hidden>
                        {emoji}
                    </span>
                </span>
            );
        }
        return (
            <span
                className="text-nowrap d-flex align-items-center justify-content-between gap-2 kyiv-weather-cell"
                title={title}
            >
                <span>{formatSignedDegrees(displayTemp)}</span>
                <span className="flex-shrink-0" style={KYIV_WEATHER_EMOJI_STYLE} aria-hidden>
                    {emoji}
                </span>
            </span>
        );
    }

    const minLbl = formatSignedDegrees(tMin);
    const maxLbl = formatSignedDegrees(tMax);
    if (minLbl && maxLbl) {
        return (
            <div
                className="small lh-sm text-nowrap d-flex align-items-center justify-content-between gap-2 kyiv-weather-cell"
                title={title}
            >
                <div className="text-start">
                    <div>мін. {minLbl}</div>
                    <div>макс. {maxLbl}</div>
                </div>
                <span className="flex-shrink-0 ps-2" style={KYIV_WEATHER_EMOJI_STYLE} aria-hidden>
                    {emoji}
                </span>
            </div>
        );
    }

    if (!Number.isFinite(displayTemp)) {
        return (
            <span
                className="text-nowrap d-flex align-items-center justify-content-between gap-2 kyiv-weather-cell"
                title={title}
            >
                <span>—</span>
                <span className="flex-shrink-0" style={KYIV_WEATHER_EMOJI_STYLE} aria-hidden>
                    {emoji}
                </span>
            </span>
        );
    }

    return (
        <span
            className="text-nowrap d-flex align-items-center justify-content-between gap-2 kyiv-weather-cell"
            title={title}
        >
            <span>{formatSignedDegrees(displayTemp)}</span>
            <span className="flex-shrink-0" style={KYIV_WEATHER_EMOJI_STYLE} aria-hidden>
                {emoji}
            </span>
        </span>
    );
}

const getPeriodLabel = (group, range) => {
    if (!group || group.length === 0) return "-";

    const sampleDate = group[0].created_at;

    const d = dayjs(sampleDate).tz(TIMEZONE);

    switch (range) {
        case "day":
            return d.format("D MMM YYYY");

        case "week":
        case "2weeks": {
            const daysCount = range === "2weeks" ? 13 : 6;
            const start = d.startOf("isoWeek");
            const end = start.add(daysCount, "day");

            // если месяц одинаковый
            if (start.format("MMMM") === end.format("MMMM")) {
                return `${start.format("D")}–${end.format("D MMMM")}`;
            }

            // если месяцы разные
            return `${start.format("D MMM")} – ${end.format("D MMM")}`;
        }

        case "month":
            return d.format("MMMM YYYY");

        case "year":
            return d.format("YYYY");

        default:
            return "-";
    }
};

const OrdersTable = ({
                         getTotalPurchased,
                         getTotalMargin,
                         productBreakdownColumns = [],
                         currency,
                         usdBuyRate,
                         usdSellRate,
                         combinedData,
                         ordersByPeriod,
                         adsByPeriod,
                         formatCurrency,
                         formatAdBudget,
                         summaries,
                         getTotalPayments,
                         getTotalUpsales,
                         getRepeatSalesWithTag5,
                         getSaleOrdersWithTag6,
                         leadsByPeriod,
                         rangeDate,
                         selectedSources,
                         ticketsByPeriod,
                         getTotalTickets,
                         getTotalLeads,
                         visibleColumns,
                         directManagersByPeriod = [],
                         instagramFollowersDisplayByPeriod = [],
                         weatherDisplayByPeriod = [],
                     }) => {
    // Общие суммы по всем периодам
    const allOrders = ordersByPeriod.flat();
    const allAds = adsByPeriod.flat();

    const totalOrders = allOrders.length;
    const totalGrandTotal = allOrders.reduce(
        (sum, order) => sum + Math.floor(order.grand_total),
        0
    );
    const totalQuantity = allOrders.reduce((orderAcc, order) => {
        return orderAcc + order.products.reduce(
            (productAcc, product) => productAcc + product.quantity,
            0
        );
    }, 0);

    const avgAdsPerPeriod =
        ordersByPeriod.length > 0 ? Math.round(allAds.length / ordersByPeriod.length) : 0;

    const getROI = (grandTotalUAH, adSpendUSD, currency = "UAH", usdRate = 40) => {
        const rate =
            usdRate != null && Number.isFinite(Number(usdRate)) && Number(usdRate) > 0
                ? Number(usdRate)
                : null;
        if (rate == null) return null;

        const spend = currency === "UAH" ? adSpendUSD * rate : adSpendUSD;

        const revenue = currency === "UAH" ? grandTotalUAH : grandTotalUAH / rate;

        if (!spend || spend === 0 || !Number.isFinite(revenue)) return null;

        const pct = ((revenue - spend) / spend) * 100;
        return Number.isFinite(pct) ? pct : null;
    };

    const totalByPid = combinedData.reduce((acc, item) => {
        if (!item.pid) return acc;

        if (!acc[item.pid]) {
            acc[item.pid] = {
                pid: item.pid,
                name: item.name,
                quantity: 0
            };
        }

        acc[item.pid].quantity += item.quantity;
        return acc;
    }, {});

    const footerQtyForBreakdownColumn = (col) => {
        if (col?.pid != null && Number.isFinite(Number(col.pid)) && totalByPid[col.pid]) {
            return totalByPid[col.pid].quantity;
        }
        const want = caseFoldForProductMatch(col?.name ?? "");
        const sum = combinedData.reduce((acc, p) => {
            const lineFold = caseFoldForProductMatch(p.name ?? "");
            if (!want || !lineFold || lineFold !== want) return acc;
            return acc + (Number(p.quantity) || 0);
        }, 0);
        return sum > 0 ? sum : "";
    };





    return (
        <div className="table-holder">
            <Table bordered className="table table-light table-sticky table-hover table-striped">
                <thead className="table-dark">
                <tr>
                    <th>Період</th>
                    {visibleColumns.kyivWeather && (
                        <th
                            className="text-nowrap"
                            title="Добова погода у Києві (Open-Meteo); зберігається в Summary після синхронізації"
                        >
                            Погода · Київ
                        </th>
                    )}
                    <th>Кількість замовлень</th>
                    {visibleColumns.instagramFollowers && (
                        <th title="Значення з БД (Summary) за день; у дужках зміна до попереднього календарного дня">
                            Підписники Instagram
                        </th>
                    )}
                    {visibleColumns.directManager && <th>Менеджер (Директ)</th>}
                    {visibleColumns.leads && <th>Ліди</th>}
                    {visibleColumns.leads && <th>Конверсія лідов, %</th>}
                    {visibleColumns.tickets && <th>Діалоги</th>}
                    {visibleColumns.tickets && <th>Конверсія діалогів, %</th>}
                    <th>Сума замовлень</th>
                    {visibleColumns.margin && <th>Маржа</th>}
                    {visibleColumns.cost && <th>Собівартість</th>}
                    {visibleColumns.payments && <th>Сума оплат</th>}
                    {visibleColumns.avgCheck && <th>Середній чек</th>}
                    {visibleColumns.ads && <th>Кількість РК</th>}
                    {visibleColumns.spend && <th>Рекламний бюджет</th>}
                    {visibleColumns.spend && <th>ROI</th>}
                    {visibleColumns.clicks && <th>Кількість кліків</th>}
                    {visibleColumns.impressions && <th>Кількість показів</th>}
                    {visibleColumns.upsales && <th>Допродаж</th>}
                    {visibleColumns.repeatSales && <th>Повторні продажі</th>}
                    {visibleColumns.saleOrders && <th>Розпродажі</th>}
                    {visibleColumns.itemsCount && <th>Кількість речей</th>}
                    {visibleColumns.productBreakdown && productBreakdownColumns.map((col, index) => (
                        <th key={index} className="vertical-header">{col.name}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {ordersByPeriod.map((periodOrders, periodIndex) => {
                    const ads = adsByPeriod[periodIndex] || [];
                    const periodTotalOrders = periodOrders.length;
                    const periodTotalSpend = ads.reduce(
                        (sum, ad) => sum + parseFloat(ad.spend || 0),
                        0
                    );

                    const periodClicks = ads.reduce(
                        (sum, ad) => sum + parseInt(ad.inline_link_clicks || ad.clicks || 0, 10),
                        0
                    );

                    const periodImpressions = ads.reduce(
                        (sum, ad) => sum + parseInt(ad.impressions || 0, 10),
                        0
                    );
                    const periodGrandTotal = periodOrders.reduce(
                        (sum, order) => sum + Math.floor(order.grand_total),
                        0
                    );

                    const periodQuantity = periodOrders.reduce((orderAcc, order) => {
                        return orderAcc + order.products.reduce(
                            (productAcc, product) => productAcc + product.quantity,
                            0
                        );
                    }, 0);

                    const productQuantities = productBreakdownColumns.map((col) =>
                        periodOrders.reduce((sum, order) => {
                            const matchingProducts = order.products.filter((p) =>
                                productLineMatchesBreakdownColumn(p, col)
                            );
                            return sum + matchingProducts.reduce((acc, p) => acc + (p.quantity || 0), 0);
                        }, 0)
                    );

                    const leads = getDayMetric(leadsByPeriod[periodIndex] || {}, selectedSources);
                    const tickets = getDayMetric(ticketsByPeriod[periodIndex] || {}, selectedSources);

                    const conversionFromLeads = leads > 0
                        ? ((periodTotalOrders / leads) * 100).toFixed(1) + "%"
                        : "-";

                    const conversionFromTickets = tickets > 0
                        ? ((periodTotalOrders / tickets) * 100).toFixed(1) + "%"
                        : "-";


                    const directManagerLabel =
                        directManagersByPeriod[periodIndex] || "";

                    return (
                        <tr key={periodIndex}>
                            <td style={{whiteSpace: "nowrap"}}>
                                {getPeriodLabel(periodOrders, rangeDate)}
                            </td>
                            {visibleColumns.kyivWeather && (
                                <td className="text-nowrap small align-middle">
                                    {formatKyivWeatherCell(weatherDisplayByPeriod[periodIndex])}
                                </td>
                            )}
                            <td>{periodTotalOrders}</td>
                            {visibleColumns.instagramFollowers && (
                                <td className="text-nowrap">
                                    {instagramFollowersDisplayByPeriod[periodIndex] ?? "—"}
                                </td>
                            )}
                            {visibleColumns.directManager && (
                                <td style={{ whiteSpace: "nowrap" }}>
                                    {directManagerLabel || "—"}
                                </td>
                            )}
                            {visibleColumns.leads && (
                                <>
                                    <td>{leads}</td>
                                    <td>{conversionFromLeads}</td>
                                </>
                            )}
                            {visibleColumns.tickets && (
                                <>
                                    <td>{tickets}</td>
                                    <td>{conversionFromTickets}</td>
                                </>
                            )}
                            <td>{formatCurrency(periodGrandTotal)}</td>
                            {visibleColumns.margin && <td>{formatCurrency(getTotalMargin(periodOrders))}</td>}
                            {visibleColumns.cost && <td>{formatCurrency(getTotalPurchased(periodOrders))}</td>}
                            {visibleColumns.payments && <td>{formatCurrency(getTotalPayments(periodOrders))}</td>}
                            {visibleColumns.avgCheck && <td>{formatCurrency(periodTotalOrders > 0 ? Math.round(periodGrandTotal / periodTotalOrders) : 0)}</td>}
                            {visibleColumns.ads && <td>{ads.length}</td>}
                            {visibleColumns.spend && <td>{formatAdBudget(periodTotalSpend)}</td>}
                            {(()=>{
                                const roi = getROI(periodGrandTotal, periodTotalSpend, currency, usdSellRate);

                                return (
                                    <>
                                        {visibleColumns.spend && (
                                            <td
                                                className={
                                                    roi != null && Number.isFinite(roi) && roi > 0
                                                        ? "text-success"
                                                        : "text-danger"
                                                }
                                            >
                                                {roi != null && Number.isFinite(roi)
                                                    ? `${roi.toFixed(1)}%`
                                                    : "—"}
                                            </td>
                                        )}
                                    </>
                                );
                            })()}
                            {visibleColumns.clicks && <td>{periodClicks}</td>}
                            {visibleColumns.impressions && <td>{periodImpressions}</td>}
                            {visibleColumns.upsales && <td>{getTotalUpsales(periodOrders)}</td>}
                            {visibleColumns.repeatSales && <td>{getRepeatSalesWithTag5(periodOrders)}</td>}
                            {visibleColumns.saleOrders && <td>{getSaleOrdersWithTag6(periodOrders)}</td>}
                            {visibleColumns.itemsCount && <td>{periodQuantity}</td>}
                            {visibleColumns.productBreakdown && productQuantities.map((quantity, index) => (
                                <td key={index}>{quantity !== 0 ? quantity : ""}</td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
                <tfoot>
                <tr>
                    <td><strong>Всього:</strong></td>
                    {visibleColumns.kyivWeather && <td />}
                    <td>{totalOrders}</td>
                    {visibleColumns.instagramFollowers && <td />}
                    {
                        (() => {
                            const totalLeads = getTotalLeads();
                            const totalTickets = getTotalTickets();
                            const totalConversionFromLeads = totalLeads > 0
                                ? ((totalOrders / totalLeads) * 100).toFixed(1) + "%"
                                : "-";
                            const totalConversionFromTickets = totalTickets > 0
                                ? ((totalOrders / totalTickets) * 100).toFixed(1) + "%"
                                : "-";

                            return (
                                <>
                                    {visibleColumns.directManager && <td />}
                                    {visibleColumns.leads && (
                                        <>
                                            <td><strong>{totalLeads}</strong></td>
                                            <td>{totalConversionFromLeads}</td>
                                        </>
                                    )}
                                    {visibleColumns.tickets && (
                                        <>
                                            <td><strong>{totalTickets}</strong></td>
                                            <td>{totalConversionFromTickets}</td>
                                        </>
                                    )}
                                </>
                            );
                        })()
                    }
                    <td>{formatCurrency(totalGrandTotal)}</td>
                    {visibleColumns.margin && <td>{formatCurrency(getTotalMargin(allOrders))}</td>}
                    {visibleColumns.cost && <td>{formatCurrency(getTotalPurchased(allOrders))}</td>}
                    {visibleColumns.payments && <td>{formatCurrency(getTotalPayments(allOrders))}</td>}
                    {visibleColumns.avgCheck && <td>{formatCurrency(totalOrders > 0 ? Math.round(totalGrandTotal / totalOrders) : 0)}</td>}
                    {visibleColumns.ads && <td>{avgAdsPerPeriod}</td>}
                    {
                        (() => {
                            const visibleAds = adsByPeriod.slice(0, ordersByPeriod.length).flat();
                            const visibleSpend = visibleAds.reduce((sum, ad) => sum + parseFloat(ad.spend || 0), 0);
                            const visibleClicks = visibleAds.reduce((sum, ad) => sum + parseInt(ad.clicks || 0, 10), 0);
                            const visibleImpressions = visibleAds.reduce((sum, ad) => sum + parseInt(ad.impressions || 0, 10), 0);
                            const totalROI = getROI(totalGrandTotal, visibleSpend, currency, usdSellRate);


                            return (
                                <>
                                    {visibleColumns.spend && <td>{formatAdBudget(visibleSpend)}</td>}
                                    {visibleColumns.spend && (
                                        <td>
                                            {totalROI != null && Number.isFinite(totalROI)
                                                ? `${totalROI.toFixed(1)}%`
                                                : "—"}
                                        </td>
                                    )}
                                    {visibleColumns.clicks && <td>{visibleClicks}</td>}
                                    {visibleColumns.impressions && <td>{visibleImpressions}</td>}
                                </>
                            );
                        })()
                    }
                    {visibleColumns.upsales && <td>{getTotalUpsales(allOrders)}</td>}
                    {visibleColumns.repeatSales && <td>{getRepeatSalesWithTag5(allOrders)}</td>}
                    {visibleColumns.saleOrders && <td>{getSaleOrdersWithTag6(allOrders)}</td>}
                    {visibleColumns.itemsCount && <td>{totalQuantity}</td>}
                    {visibleColumns.productBreakdown && productBreakdownColumns.map((col, index) => {
                        const qty = footerQtyForBreakdownColumn(col);
                        return (
                            <td key={index}>
                                <strong>{qty === "" || qty == null ? "" : qty}</strong>
                            </td>
                        );
                    })}
                </tr>
                </tfoot>
            </Table>
        </div>
    );
};

export default OrdersTable;
